import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { LogOut } from 'lucide-react'
import { clearLessonSession } from '@/lib/lessonSessionStorage'
import { authApi } from '@/api/auth'
import { Button } from '@/components/ui/Button'
import { useMinCopy } from '@/i18n/useUiLocale'
import { useSettingsStore } from '@/store/settingsStore'
import { useAuthStore } from '@/store/authStore'
import { cn } from '@/components/ui/cn'

interface LogoutButtonProps {
  variant?: 'default' | 'ghost' | 'outline'
  className?: string
  showIcon?: boolean
  fullWidth?: boolean
}

export function LogoutButton({
  variant: _variant = 'ghost',
  className,
  showIcon = true,
  fullWidth = false,
}: LogoutButtonProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const userId = useAuthStore((state) => state.user?.id)
  const clearAuth = useAuthStore((state) => state.clearAuth)
  const clearSettings = useSettingsStore((state) => state.clearSettings)
  const logoutLabel = useMinCopy().nav.logout

  const logoutMutation = useMutation({
    mutationFn: () => authApi.logout(),
    onSettled: () => {
      if (userId) clearLessonSession(userId)
      clearAuth()
      clearSettings()
      queryClient.clear()
      navigate('/', { replace: true })
    },
  })

  return (
    <Button
      type="button"
      className={cn(fullWidth && 'w-full', showIcon && 'gap-2', className)}
      loading={logoutMutation.isPending}
      onClick={() => logoutMutation.mutate()}
      data-testid="logout-button"
    >
      {showIcon ? <LogOut className="h-4 w-4" /> : null}
      {logoutLabel}
    </Button>
  )
}
